import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { IcoChevronLeft } from "../../components/icons";
import { AppLayout } from "../../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  RadioCard,
  RadioCardContent,
  TextArea,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, MemberRole, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";

const MAX_INVITE_EMAILS = 50;

type InvitePageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
};

export async function load(args: LoaderArgs): Promise<InvitePageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Only admins can invite members
  if (!isAdmin(orgResult.currentMember.role)) {
    throw new RedirectError("/");
  }

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
  };
}

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Invite Members");
  const { currentUser, mimic, orgId, orgName, brandColor } = state.value;
  const isSubmitting = useSignal(false);
  const errors = useSignal({}) as FormErrors;
  const emailCount = useSignal(0);
  const emailsValue = useSignal("");
  const selectedRole = useSignal<MemberRole>("member");

  function handleEmailsInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    emailsValue.value = target.value;
    emailCount.value = parseEmails(target.value).length;
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (isSubmitting.value) return;

    const emails = parseEmails(emailsValue.value);

    if (emails.length === 0) {
      errors.value = { emails: "Enter at least one email address" };
      return;
    }

    if (emails.length > MAX_INVITE_EMAILS) {
      errors.value = {
        emails: `Maximum ${MAX_INVITE_EMAILS} emails per request (you have ${emails.length})`,
      };
      return;
    }

    // Basic email validation
    const invalidEmails = emails.filter((e) => !e.includes("@"));
    if (invalidEmails.length > 0) {
      errors.value = {
        emails: `Invalid email: ${invalidEmails[0]}`,
      };
      return;
    }

    isSubmitting.value = true;
    errors.value = {};

    try {
      const result = await rpc.orgs.inviteMembers({
        orgId,
        emails,
        role: selectedRole.value,
      });
      if (result.alreadyMembers.length > 0) {
        showError(`Already members: ${result.alreadyMembers.join(", ")}`);
      }
      navigateTo(`/orgs/${orgId}/members`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSubmitting.value = false;
    }
  }

  const isOverLimit = emailCount.value > MAX_INVITE_EMAILS;
  const isNearLimit = emailCount.value > MAX_INVITE_EMAILS * 0.8;

  return (
    <AppLayout user={currentUser} mimic={mimic}>
      <div class="flex justify-center" data-brand={brandColor}>
        <div class="w-full max-w-lg flex flex-col gap-4">
          <a
            href={`/orgs/${orgId}/members`}
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back to Members
          </a>

          {/* Card wrapper for form */}
          <div class="rounded-xl bg-surface p-8 border border-border">
            <div class="mb-6">
              <h1 class="text-2xl font-bold text-text mb-2">Invite Members</h1>
              <p class="text-text-muted">
                Invite people to join{" "}
                <strong class="text-text">{orgName}</strong>
              </p>
            </div>

            <Form
              errors={errors}
              onSubmit={handleSubmit}
              class="flex flex-col gap-6"
            >
              {/* Email Addresses */}
              <div class="flex flex-col gap-1">
                <span
                  id="emails-label"
                  class="text-sm font-medium text-text-secondary"
                >
                  Email Addresses
                </span>
                <TextArea
                  name="emails"
                  value={emailsValue.value}
                  placeholder="Enter email addresses, one per line or comma-separated"
                  class="block w-full rounded-lg border border-border px-3 py-2 bg-surface text-text placeholder:text-text-subtle hover:border-text-subtle focus:ring-ring transition-colors"
                  autofocus
                  onInput={handleEmailsInput}
                  aria-labelledby="emails-label"
                  aria-describedby={
                    errors.value.emails ? "emails-error" : undefined
                  }
                  aria-invalid={errors.value.emails ? true : undefined}
                />
                <div class="flex items-center justify-between">
                  {errors.value.emails ? (
                    <p
                      id="emails-error"
                      role="alert"
                      class="text-sm text-red-400"
                    >
                      {errors.value.emails}
                    </p>
                  ) : (
                    <span />
                  )}
                  {emailCount.value > 0 && (
                    <span
                      class={`text-xs ${isOverLimit ? "text-red-400" : isNearLimit ? "text-yellow-400" : "text-text-muted"}`}
                      aria-live="polite"
                    >
                      {emailCount.value} / {MAX_INVITE_EMAILS}
                    </span>
                  )}
                </div>
              </div>

              {/* Role Selection */}
              <fieldset class="flex flex-col gap-2">
                <legend class="text-sm font-medium text-text-secondary mb-1">
                  Role
                </legend>
                <RadioCard
                  name="role"
                  value="member"
                  checked={selectedRole.value === "member"}
                  onChange={() => {
                    selectedRole.value = "member";
                  }}
                  compact
                >
                  <RadioCardContent
                    label="Member"
                    description="Can view and submit forms"
                  />
                </RadioCard>
                <RadioCard
                  name="role"
                  value="admin"
                  checked={selectedRole.value === "admin"}
                  onChange={() => {
                    selectedRole.value = "admin";
                  }}
                  compact
                >
                  <RadioCardContent
                    label="Admin"
                    description="Can manage members and forms"
                  />
                </RadioCard>
              </fieldset>

              {/* Actions - left aligned: submit first, then cancel */}
              <div class="flex gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting.value}
                >
                  {isSubmitting.value ? "Sending..." : "Send Invitations"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigateTo(`/orgs/${orgId}/members`)}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
